/**
 * Avatar modal — dwa panele:
 *   "picker" — wybór predefiniowanego avatara (obsługa presetów w onboarding/index.js)
 *              + przycisk uploadu własnego zdjęcia.
 *   "crop"   — file picker + Cropperjs (1:1) → 512×512 WebP/JPEG → upload przez
 *              presigned URL (direct-to-S3); fallback na POST gdy tryb local.
 *
 * Exports:
 *   openAvatarModal(rootEl, uploadUrl, onSaved) — otwiera modal (panel picker).
 *   setSelectedAvatar(filename, url)            — ustawia wybór (np. preset) i zamyka modal.
 *   getAvatarFilename() / getAvatarUrl()
 */

import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { uploadViaPresigned } from '../../upload-helper.js';

let cropper = null;
let pendingBlob = null;       // skadrowany blob — wysyłany na S3 dopiero przy finalnym submit
let presetKey = null;         // wybrany preset (marker) — bez uploadu
let previewUrl = null;        // URL do podglądu (lokalny blob albo asset presetu)
let uploadedFilename = null;  // cache nazwy po wysłaniu (retry submit bez ponownego uploadu)
let uploadUrl = null;
let onSavedCb = null;
let modalRoot = null;
let keydownHandler = null;
let uploading = false;

function el(selector) {
    return modalRoot.querySelector(selector);
}

function showError(msg) {
    const e = el('[data-avatar-error]');
    if (!e) return;
    e.textContent = msg;
    e.hidden = false;
}

function clearError() {
    const e = el('[data-avatar-error]');
    if (!e) return;
    e.textContent = '';
    e.hidden = true;
}

function showPanel(name) {
    modalRoot.querySelectorAll('[data-avatar-panel]').forEach(p => {
        p.hidden = p.dataset.avatarPanel !== name;
    });
}

function resetUI() {
    clearError();
    if (cropper) { cropper.destroy(); cropper = null; }
    showPanel('picker');
    const img = el('[data-avatar-crop-img]');
    if (img) img.removeAttribute('src');
    const fileInput = el('[data-avatar-file]');
    if (fileInput) fileInput.value = '';
}

function openModal(root) {
    const modal = root.querySelector('[data-avatar-modal]');
    if (!modal) return;
    modalRoot = modal;
    resetUI();
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    keydownHandler = (e) => {
        if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', keydownHandler);
}

function closeModal() {
    if (!modalRoot) return;
    modalRoot.hidden = true;
    document.body.style.overflow = '';
    if (cropper) { cropper.destroy(); cropper = null; }
    if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
    }
}

function triggerUpload() {
    if (!modalRoot) return;
    clearError();
    const fileInput = el('[data-avatar-file]');
    if (fileInput) fileInput.click();
}

function fileExt(name) {
    const m = /\.([a-z0-9]+)$/i.exec(name || '');
    return m ? m[1].toLowerCase() : '';
}

// Blob → data: URL. Podgląd musi iść jako data:, nie blob:, bo CSP na środowiskach
// (beta) blokuje img-src blob: i skadrowane zdjęcie nie renderowało się w podglądzie.
function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
}

function isAllowedFile(file) {
    const allowedMimes = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/webp'];
    if (file.type && allowedMimes.includes(file.type.toLowerCase())) return true;
    // iOS Safari / niektóre aparaty zwracają pusty file.type — fallback na rozszerzenie
    const allowedExt = ['jpg', 'jpeg', 'png', 'webp'];
    if (!file.type && allowedExt.includes(fileExt(file.name))) return true;
    return false;
}

function onFilePicked(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    clearError();

    if (!isAllowedFile(file)) {
        const typeInfo = file.type || `.${fileExt(file.name) || '?'}`;
        showError(`Format pliku nieobsługiwany (JPG, PNG, WebP). Wykryty typ: ${typeInfo}.`);
        return;
    }
    if (file.size > 5 * 1024 * 1024) {
        showError('Plik za duży (max 5 MB).');
        return;
    }

    const reader = new FileReader();
    const img = el('[data-avatar-crop-img]');
    img.onload = null;
    img.onerror = null;
    showPanel('crop');

    reader.onerror = () => {
        resetUI();
        showError('Nie udało się odczytać pliku. Spróbuj innego zdjęcia.');
    };

    reader.onload = () => {
        img.src = reader.result;
    };

    reader.readAsDataURL(file);

    img.onload = () => {
        if (cropper) cropper.destroy();
        try {
            cropper = new Cropper(img, {
                aspectRatio: 1,
                // viewMode 1 → całe zdjęcie widoczne, kadr (koło) w środku; user przesuwa zdjęcie.
                viewMode: 1,
                dragMode: 'move',
                cropBoxResizable: false,
                cropBoxMovable: false,
                toggleDragModeOnDblclick: false,
                autoCropArea: 1,
                modal: false,        // własny "reflektor" (SCSS ::before) robi przyciemnienie
                background: false,
                guides: false,
                center: false,
                highlight: false,
                responsive: true,
                ready() {
                    // Kadr = koło wewnętrzne pierścienia (434.93/780 = 55.8% mniejszego wymiaru), wyśrodkowany.
                    if (!cropper) return;
                    const cd = cropper.getContainerData();
                    const size = Math.min(cd.width, cd.height) * 0.558;
                    cropper.setCropBoxData({
                        width: size,
                        height: size,
                        left: (cd.width - size) / 2,
                        top: (cd.height - size) / 2,
                    });
                },
            });
        } catch (e) {
            resetUI();
            showError('Nie udało się otworzyć edytora kadrowania. Spróbuj innego zdjęcia.');
        }
    };

    img.onerror = () => {
        resetUI();
        const typeInfo = file.type || `.${fileExt(file.name) || '?'}`;
        showError(`Nie udało się wczytać zdjęcia (typ: ${typeInfo}). Spróbuj zapisać je jako JPG lub PNG i wgrać ponownie.`);
    };
}

async function saveCrop() {
    if (!cropper || uploading) return;
    uploading = true;
    clearError();

    try {
        const canvas = cropper.getCroppedCanvas({
            width: 512,
            height: 512,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
        });

        const blob = await new Promise((resolve) => {
            canvas.toBlob(
                (b) => {
                    if (b) { resolve(b); return; }
                    canvas.toBlob((j) => resolve(j), 'image/jpeg', 0.85);
                },
                'image/webp',
                0.85,
            );
        });

        if (!blob) {
            showError('Nie udało się przetworzyć obrazu.');
            return;
        }

        // NIE wysyłamy teraz — trzymamy blob + podgląd. Upload na S3 dopiero przy finalnym
        // submit (commitAvatar), żeby porzucone/wielokrotne kadry nie zaśmiecały bucketa.
        pendingBlob = blob;
        presetKey = null;
        uploadedFilename = null;
        if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        try {
            previewUrl = await blobToDataUrl(blob);
        } catch {
            previewUrl = URL.createObjectURL(blob); // ostateczny fallback
        }
        closeModal();
        if (onSavedCb) onSavedCb();
    } finally {
        uploading = false;
    }
}

// Public API ────────────────────────────────────────────────────────────────

let listenersAttached = false;

function attachGlobalListenersOnce() {
    if (listenersAttached) return;
    listenersAttached = true;

    document.addEventListener('change', (e) => {
        if (e.target.matches('[data-avatar-file]')) onFilePicked(e);
    });

    document.addEventListener('click', (e) => {
        const actionEl = e.target.closest('[data-action]');
        if (!actionEl) return;
        const action = actionEl.dataset.action;
        if (action === 'close-avatar-modal') { closeModal(); return; }
        if (action === 'avatar-pick-upload') { triggerUpload(); return; }
        if (action === 'avatar-save')        { saveCrop(); return; }
    });
}

export function openAvatarModal(root, url, onSaved) {
    uploadUrl = url;
    onSavedCb = onSaved;
    attachGlobalListenersOnce();
    openModal(root);
}

// Wybór presetu — bez uploadu (statyczny asset). Marker idzie wprost na submit.
export function setSelectedAvatar(key, url) {
    pendingBlob = null;
    uploadedFilename = null;
    presetKey = key;
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    previewUrl = url;
    closeModal();
    if (onSavedCb) onSavedCb();
}

// Czyści wybór avatara (preset, skadrowany blob, cache wysyłki). Wołane przy zmianie
// drużyny: presety są per-drużyna, więc po zmianie wynik znów pokazuje domyślną ikonę,
// a zdjęcie profilowe zmieni się dopiero po ponownym wyborze/wgraniu.
export function resetAvatarSelection() {
    pendingBlob = null;
    presetKey = null;
    uploadedFilename = null;
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    previewUrl = null;
}

// URL do podglądu w widoku wyniku (blob skadrowanego zdjęcia albo asset presetu).
export function getAvatarPreviewUrl() {
    return previewUrl;
}

/**
 * Wywoływane na finalnym submit. Wysyła skadrowany blob na S3 (presigned, fallback POST)
 * i zwraca nazwę/marker do zapisania w user.avatar.
 * @returns {Promise<string|null>} '' = brak avatara, string = nazwa/marker, null = błąd uploadu.
 */
export async function commitAvatar() {
    if (presetKey) return presetKey;            // preset — bez uploadu
    if (uploadedFilename) return uploadedFilename; // już wysłany (retry submit)
    if (!pendingBlob) return '';                // brak wyboru

    // 1) Presigned direct-to-S3 (jak misje specjalne).
    const key = await uploadViaPresigned(pendingBlob, 'avatar');
    if (key) {
        uploadedFilename = key.replace(/^avatars\//, '');
        return uploadedFilename;
    }

    // 2) Fallback: tryb local — POST.
    const ext = pendingBlob.type === 'image/webp' ? 'webp' : 'jpg';
    const fd = new FormData();
    fd.append('avatar', pendingBlob, `avatar.${ext}`);
    try {
        const res = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: fd,
        });
        if (!res.ok) return null;
        const data = await res.json();
        uploadedFilename = data.filename;
        return uploadedFilename;
    } catch {
        return null;
    }
}
